import React, { useState } from 'react';
import { Phone, Mail, MapPin, ExternalLink, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo } from '../data/mockData';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Contact = () => {
  const { language, t } = useLanguage();
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success(language === 'bn' ? 'কপি হয়েছে!' : 'Copied!');
    setTimeout(() => setCopied(null), 2000);
  };

  const contactMethods = [
    {
      icon: Phone,
      label: t('phone'),
      value: contactInfo.phone,
      href: `tel:${contactInfo.phone}`,
      color: 'from-green-500 to-emerald-600',
      type: 'phone'
    },
    {
      icon: Mail,
      label: t('email'),
      value: contactInfo.email,
      href: `mailto:${contactInfo.email}`,
      color: 'from-blue-500 to-cyan-600',
      type: 'email'
    },
    {
      icon: MapPin,
      label: t('address'),
      value: contactInfo.address[language],
      href: contactInfo.mapUrl,
      color: 'from-red-500 to-pink-600',
      type: 'address'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="h-px w-12 bg-orange-400" />
              <div className="w-2 h-2 rotate-45 bg-orange-400" />
              <div className="h-px w-12 bg-orange-400" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {t('contactTitle')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('contactSubtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Cards */}
            <div className="space-y-6">
              {contactMethods.map((method, index) => (
                <Card 
                  key={index}
                  className="border-2 border-orange-100 hover:border-orange-300 shadow-md hover:shadow-xl transition-all duration-300 bg-white group"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${method.color} flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform`}>
                        <method.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-500 mb-2">
                          {method.label}
                        </h3>
                        <p className="text-gray-800 font-medium mb-3 break-words">
                          {method.value}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => window.open(method.href, '_blank')}
                            size="sm"
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {method.type === 'address' ? t('getDirections') : 
                             language === 'bn' ? 'যোগাযোগ করুন' : 'Contact'}
                          </Button>
                          <Button
                            onClick={() => copyToClipboard(method.value, method.type)}
                            size="sm"
                            variant="outline"
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                          >
                            {copied === method.type ? (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                {language === 'bn' ? 'কপি হয়েছে' : 'Copied'}
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {language === 'bn' ? 'কপি' : 'Copy'}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Google Profile Card */}
              <Card className="border-2 border-orange-100 hover:border-orange-300 shadow-md hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-orange-50 to-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {language === 'bn' ? 'গুগল প্রোফাইল' : 'Google Profile'}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    {language === 'bn' 
                      ? 'আমাদের গুগল প্রোফাইলে ১৯০+ রিভিউ দেখুন' 
                      : 'View 190+ reviews on our Google profile'}
                  </p>
                  <Button
                    onClick={() => window.open(contactInfo.googleProfile, '_blank')}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {language === 'bn' ? 'প্রোফাইল দেখুন' : 'View Profile'}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Map or Image */}
            <div className="space-y-6">
              <div className="relative h-96 lg:h-full min-h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1690271649975-f333ae373d8d?w=800&q=80"
                  alt="Vrindavan Location"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-8">
                  <div className="text-white">
                    <h3 className="text-2xl font-bold mb-3">
                      {language === 'bn' ? 'আমরা এখানে আছি' : 'Find Us Here'}
                    </h3>
                    <p className="text-orange-200 mb-4">
                      {contactInfo.address[language]}
                    </p>
                    <Button
                      onClick={() => window.open(contactInfo.mapUrl, '_blank')}
                      variant="outline"
                      className="border-white text-white hover:bg-white/10"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      {t('getDirections')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
